
import { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Mail, Send } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useI18n } from '@/i18n/LanguageContext';

export const NewsletterSubscription = ({ variant = "card" }: { variant?: "card" | "inline" }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { t } = useI18n();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes('@')) {
      toast({ title: t.newsletter.errorTitle, description: t.newsletter.invalid, variant: "destructive" });
      return;
    }

    setIsLoading(true);

    try {
      // Check if email already exists
      const { data: existingSubscription, error: checkError } = await supabase
        .from('newsletter_subscribers')
        .select('id, is_active')
        .eq('email', email)
        .maybeSingle();

      if (checkError) {
        console.error('Error checking existing subscription:', checkError);
        throw checkError;
      }

      if (existingSubscription) {
        if (existingSubscription.is_active) {
          toast({ title: t.newsletter.alreadyTitle, description: t.newsletter.alreadyText, variant: "destructive" });
        } else {
          // Reactivate subscription
          const { error: updateError } = await supabase
            .from('newsletter_subscribers')
            .update({ is_active: true, subscribed_at: new Date().toISOString() })
            .eq('id', existingSubscription.id);

          if (updateError) {
            console.error('Error reactivating subscription:', updateError);
            throw updateError;
          }

          toast({ title: t.newsletter.successTitle, description: t.newsletter.resubscribed });
        }
      } else {
        // Create new subscription
        const { error: insertError } = await supabase
          .from('newsletter_subscribers')
          .insert({ email, is_active: true, subscribed_at: new Date().toISOString() });

        if (insertError) {
          console.error('Error creating subscription:', insertError);
          throw insertError;
        }

        toast({ title: t.newsletter.successTitle, description: t.newsletter.subscribed });
      }

      setEmail('');
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast({ title: t.newsletter.errorTitle, description: t.newsletter.genericError, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  if (variant === "inline") {
    return (
      <form onSubmit={handleSubscribe} className="mt-5 max-w-sm">
        <p className="mb-2 text-xs font-bold uppercase tracking-wide text-foreground">{t.newsletter.title}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <label htmlFor="footer-newsletter-email" className="sr-only">{t.newsletter.placeholder}</label>
          <Input
            id="footer-newsletter-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t.newsletter.placeholder}
            className="h-10 bg-background"
            required
          />
          <Button type="submit" className="h-10 shrink-0" disabled={isLoading}>
            {isLoading ? t.newsletter.subscribing : t.newsletter.subscribe}
            <Send className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="app-card p-6 hover:translate-y-0">
      <div className="mb-4 text-center">
        <div className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary">
          <Mail className="h-6 w-6 text-primary-foreground" aria-hidden="true" />
        </div>
        <h3 className="mb-2 font-display text-xl font-bold text-highlight">{t.newsletter.title}</h3>
        <p className="text-sm text-muted-foreground">{t.newsletter.text}</p>
      </div>

      <form onSubmit={handleSubscribe} className="space-y-3">
        <label htmlFor="newsletter-email" className="sr-only">{t.newsletter.placeholder}</label>
        <Input
          id="newsletter-email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t.newsletter.placeholder}
          className="h-11 bg-background"
          required
        />
        <Button type="submit" className="h-11 w-full" disabled={isLoading}>
          {isLoading ? t.newsletter.subscribing : t.newsletter.subscribe}
          <Send className="ml-2 h-4 w-4" aria-hidden="true" />
        </Button>
      </form>
    </div>
  );
};
